import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";

import {
  cn,
  getAppStoreName,
  getDateFromTimestamp,
  getOfferFrequency,
} from "@/utils";
import { Button } from "@/components";
import {
  PRODUCT_PLATFORM_ANDROID,
  PRODUCT_PLATFORM_APPLE,
  PRODUCT_PLATFORM_MANUAL,
} from "@/const.ts";
import { useAppProperties, useOffers } from "@/hooks";
import { useTranslate, useUser } from "@/providers/context";
import { CreditCard, TicketVoucher, Upgrade } from "@/icons";

import { useStripeUser } from "../hooks/useStripeUser.tsx";
import { Section } from "./Section.tsx";
import { ExternalPlan } from "./ExternalPlan.tsx";

export const YourPlan = () => {
  const { platform, detailed_products } = useUser();
  const { data: properties } = useAppProperties();
  const { translate } = useTranslate();
  const navigate = useNavigate();
  const { getOfferBySourceProductId, getOfferByPriceId } = useOffers();
  const { activeSubscription: stripeSubscription } = useStripeUser();

  const hasAppSubscription = [
    PRODUCT_PLATFORM_APPLE,
    PRODUCT_PLATFORM_ANDROID,
  ].includes(platform);

  const subscriptionOffer = useMemo(() => {
    // Stripe subscription takes precedence
    if (stripeSubscription) {
      const offer = getOfferByPriceId(stripeSubscription.PriceID);
      if (offer) {
        return offer;
      }
    }

    if (!detailed_products || detailed_products.length === 0) return undefined;
    for (const product of detailed_products) {
      if (product.old_platform) {
        continue; // Skip external migrated offers
      }
      const offer = getOfferBySourceProductId(product.source_product_id);
      if (offer) {
        return offer;
      }
    }
    return undefined;
  }, [
    detailed_products,
    getOfferByPriceId,
    getOfferBySourceProductId,
    stripeSubscription,
  ]);

  const currencySymbol = properties?.WebDefaultCurrencySymbol || "$";

  const coupon = stripeSubscription?.Discount?.Coupon;

  const adjustedPrice = useMemo(() => {
    if (!coupon || !stripeSubscription?.PriceAmount) {
      return undefined;
    }

    if (coupon.PercentOff) {
      return stripeSubscription.PriceAmount * (1 - coupon.PercentOff / 100);
    }

    if (coupon.AmountOff) {
      return stripeSubscription.PriceAmount - coupon.AmountOff / 100;
    }

    return undefined;
  }, [stripeSubscription?.PriceAmount, coupon]);

  const displayPrice = useMemo(() => {
    if (!stripeSubscription) {
      return undefined;
    }

    const price = adjustedPrice ?? stripeSubscription.PriceAmount;

    if (properties?.WebDefaultCurrency === "JPY") {
      return price.toFixed(0);
    } else {
      return price.toFixed(2);
    }
  }, [adjustedPrice, stripeSubscription, properties?.WebDefaultCurrency]);

  const externalSubscriptions = detailed_products?.filter((product) => {
    if (product.old_platform === "Resubscribe") {
      return detailed_products.length === 1; // Only show Resubscribe product if they haven't already resubscribed through stripe
    }
    return product.platform === "Manual" && !!product.old_platform;
  });

  // Group subscriptions by platform
  const groupedExternalSubscriptions = useMemo(() => {
    if (!externalSubscriptions) return {};
    return externalSubscriptions.reduce(
      (acc, subscription) => {
        const platform = subscription.old_platform ?? "";
        if (!acc[platform]) {
          acc[platform] = [];
        }
        acc[platform].push({
          oldProductId: subscription.old_product_id ?? "",
          sourceProductId: subscription.source_product_id ?? "",
          endTs: subscription.end_ts,
        });
        return acc;
      },
      {} as Record<
        string,
        { oldProductId: string; endTs?: number; sourceProductId: string }[]
      >,
    );
  }, [externalSubscriptions]);

  const hasExternalSubscriptions = useMemo(() => {
    return !!externalSubscriptions && externalSubscriptions?.length > 0;
  }, [externalSubscriptions]);

  return (
    <Section heading={translate("Your Plan")}>
      {/* Hide this section if there are external subscriptions, unless there is an actual Stripe or Mobile subscription */}
      {(!externalSubscriptions?.length || subscriptionOffer) && (
        <div className="rounded-xl border border-border px-5 py-6">
          <div className="h1 flex items-center justify-between pb-4">
            {/* Plan Title */}
            {subscriptionOffer ? (
              <div>{subscriptionOffer.FinalDisplayName}</div>
            ) : (
              <div>{translate("No active subscription")}</div>
            )}
            {/* Plan Price and Frequency */}
            {stripeSubscription && (
              <div>
                <span className={cn("h1")}>
                  {currencySymbol}
                  {displayPrice}
                </span>
                <span className="h5-semibold">
                  {"/"}
                  {getOfferFrequency(
                    `${stripeSubscription.IntervalCount}${stripeSubscription.Interval.charAt(0)}`,
                    translate,
                    true,
                  )}
                </span>
              </div>
            )}
          </div>
          {/* Plan Subtitle and Actions */}
          {stripeSubscription ? (
            <>
              {/* The user has a web-based subscription */}
              <div className="p2-regular flex flex-col pb-8">
                {stripeSubscription.Status === "trial" && (
                  <>
                    {translate("You're on a trial")}
                    {". "}
                  </>
                )}
                {stripeSubscription.CancelAtPeriodEnd ? (
                  <>
                    {translate("Pending cancellation")}
                    {". "}
                    {translate("You will have access until")}{" "}
                  </>
                ) : (
                  <>
                    {translate("Your next bill is")} {currencySymbol}
                    {displayPrice} on{" "}
                  </>
                )}
                {format(
                  getDateFromTimestamp(stripeSubscription.CurrentPeriodEnd),
                  "dd/MM/yy",
                )}
                {coupon && (
                  <Button
                    size="s"
                    variant="edit"
                    className="mt-5 w-fit px-3"
                    onClick={() =>
                      window.open(`/manage-subscription`, "_blank")
                    }
                    icon={<TicketVoucher className="h-5 w-5" />}
                    iconPosition="left"
                  >
                    {`${translate("Active Coupon")}: ${coupon.Name}`}
                  </Button>
                )}
              </div>

              <div className="p2-regular border-t border-border pt-8">
                <Button
                  size="s"
                  variant="tertiaryHighlight"
                  onClick={() => window.open(`/manage-subscription`, "_blank")}
                  icon={<CreditCard />}
                  iconPosition="left"
                >
                  {translate("Manage My Subscription")}
                </Button>
              </div>
            </>
          ) : hasAppSubscription && subscriptionOffer ? (
            <div className="p2-medium mt-4 rounded-[4px] bg-surface-secondary p-3">
              {/* The user has a mobile-based subscription */}
              {translate("To manage your subscription go to the")}{" "}
              {properties?.AppleTitle} {translate("in the")}{" "}
              {translate(getAppStoreName(platform))}{" "}
              {translate("on your mobile or tablet device")}
              {"."}
            </div>
          ) : platform === PRODUCT_PLATFORM_MANUAL ? (
            <>
              {/* The user has been granted the offer in VidApp*/}
              <div className="pb-8">
                {translate(
                  subscriptionOffer?.WebPriceUSD === 0
                    ? "Only some content is included in your free access"
                    : "Your subscription is complimentary",
                )}
                {"."}
              </div>

              <div className="p2-regular border-t border-border pt-8">
                <Button
                  size="s"
                  variant="tertiaryHighlight"
                  onClick={() => navigate("/for-sale")}
                  icon={<Upgrade />}
                  iconPosition="left"
                >
                  {translate("Upgrade Plan")}
                </Button>
              </div>
            </>
          ) : (
            <div>
              {/* The user does not have any offers */}
              <span
                className="p2-semibold cursor-pointer text-highlightPrimary"
                onClick={() => navigate("/for-sale")}
              >
                {translate("Click here")}
              </span>{" "}
              {translate("to renew or start a new plan")}
              {"."}
            </div>
          )}
        </div>
      )}
      {hasExternalSubscriptions && (
        <>
          {Object.entries(groupedExternalSubscriptions).map(
            ([platform, products]) => (
              <ExternalPlan
                key={platform}
                oldPlatform={platform}
                products={products}
              />
            ),
          )}
        </>
      )}
    </Section>
  );
};
